import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../api/client";
import type { Metrics, Part } from "../api/types";
import { Card, cx, PageHeader, Select, Skeleton } from "../components/Controls";
import { DateRange } from "../components/DateRange";
import { addDays, endIso, fmtNum, fmtPct, localDateStr, startIso } from "../lib/format";

const PERIODS = [
  ["today", "Hoy"], ["yesterday", "Ayer"], ["week", "Esta semana"], ["7d", "Últimos 7 días"], ["30d", "Últimos 30 días"], ["custom", "Personalizado"],
];

function periodRange(p: string, custom: [string, string]): [string, string] {
  const today = localDateStr();
  if (p === "yesterday") return [addDays(today, -1), addDays(today, -1)];
  if (p === "week") {
    const dow = (new Date().getDay() + 6) % 7;
    return [addDays(today, -dow), today];
  }
  if (p === "7d") return [addDays(today, -6), today];
  if (p === "30d") return [addDays(today, -29), today];
  if (p === "custom") return custom;
  return [today, today];
}

function StatCard({ label, value, sub, testId, muted }: { label: string; value: React.ReactNode; sub?: React.ReactNode; testId: string; muted?: boolean }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className={cx("mt-2 font-display font-bold", muted ? "text-lg font-semibold text-slate-400" : "text-4xl")}>{value}</div>
      {sub && <div className="text-sm text-slate-500">{sub}</div>}
    </Card>
  );
}

export default function MetricsPage() {
  const [period, setPeriod] = useState("7d");
  const [custom, setCustom] = useState<[string, string]>([addDays(localDateStr(), -6), localDateStr()]);
  const [partId, setPartId] = useState("");
  const [parts, setParts] = useState<Part[]>([]);
  const [m, setM] = useState<Metrics | null>(null);
  const [failed, setFailed] = useState(false);
  const [from, to] = useMemo(() => periodRange(period, custom), [period, custom]);

  useEffect(() => {
    api.get<Part[]>("/parts", { active: "all" }).then(setParts).catch(() => undefined);
  }, []);
  useEffect(() => {
    setM(null);
    setFailed(false);
    api.get<Metrics>("/metrics", { from: startIso(from), to: endIso(to), part_id: partId }).then(setM).catch(() => setFailed(true));
  }, [from, to, partId]);

  const noAi = m && m.ai_inspections === 0;
  return (
    <div className="animate-rise space-y-6">
      <PageHeader title="Indicadores" subtitle="Volumen, rechazos y discrepancias del período." />
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex flex-wrap gap-1 rounded-xl border border-line bg-white p-1">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} className={cx("rounded-lg px-3 py-1.5 text-sm font-medium", period === k ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100")} data-testid={`period-${k}`}>{l}</button>
          ))}
        </div>
        {period === "custom" && <DateRange from={custom[0]} to={custom[1]} onChange={(f, t) => setCustom([f, t])} />}
        <Select value={partId} onChange={(e) => setPartId(e.target.value)} className="lg:w-64" data-testid="metrics-part-select">
          <option value="">Todas las piezas</option>
          {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} · {p.name}</option>)}
        </Select>
      </div>
      {failed ? <p className="text-sm text-ng" data-testid="metrics-error">No se pudieron cargar los indicadores.</p> : !m ? <div className="grid gap-3 sm:grid-cols-3"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div> : (
        <>
          <div className="stagger grid grid-cols-2 gap-3 lg:grid-cols-6">
            <StatCard label="Inspecciones" value={m.inspections} testId="metric-inspections" />
            <StatCard label="Piezas" value={m.pieces} testId="metric-pieces" />
            {noAi ? <StatCard label="NG según IA" value="Sin datos de IA" muted testId="metric-ai-ng" /> : <StatCard label="NG según IA" value={m.ai_ng} sub={fmtPct(m.ai_ng_pct)} testId="metric-ai-ng" />}
            <StatCard label="NG según operario" value={m.verdict_ng} sub={fmtPct(m.verdict_ng_pct)} testId="metric-verdict-ng" />
            {noAi ? <StatCard label="Discrepancias" value="Sin datos de IA" muted testId="metric-discrepancies" /> : <StatCard label="Discrepancias" value={m.discrepancies} sub={fmtPct(m.discrepancy_pct)} testId="metric-discrepancies" />}
            <StatCard label="Latencia media" value={m.avg_latency_ms === null ? "—" : `${m.avg_latency_ms} ms`} testId="metric-latency" />
          </div>
          <Card className="p-5" data-testid="metrics-by-day-chart">
            <h3 className="mb-4 font-display text-lg font-bold">Inspecciones y NG por día</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={m.by_day}>
                  <CartesianGrid vertical={false} stroke="#DDE2E8" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="inspections" name="Inspecciones" fill="#1C2431" radius={[4, 4, 0, 0]} />
                  {!noAi && <Bar dataKey="ai_ng" name="NG IA" fill="#C42A1D" radius={[4, 4, 0, 0]} />}
                  <Bar dataKey="verdict_ng" name="NG operario" fill="#B45309" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="overflow-x-auto" data-testid="metrics-by-point-table">
            <h3 className="px-5 pt-4 font-display text-lg font-bold">Por punto de inspección</h3>
            <table className="mt-2 w-full text-sm">
              <thead className="text-xs text-slate-500"><tr><th className="px-5 py-2 text-left">Punto</th><th className="px-3 text-right">Inspecciones</th><th className="px-3 text-right">NG IA</th><th className="px-3 text-right">NG operario</th><th className="px-5 text-right">% NG</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {m.by_point.map((r) => (
                  <tr key={r.point_index}>
                    <td className="px-5 py-2">{r.point_index + 1} · {r.point_name}</td>
                    <td className="px-3 text-right font-mono">{r.inspections}</td>
                    <td className="px-3 text-right font-mono">{noAi ? "—" : r.ai_ng}</td>
                    <td className="px-3 text-right font-mono">{r.verdict_ng}</td>
                    <td className="px-5 text-right font-mono">{r.inspections ? `${fmtNum((r.verdict_ng / r.inspections) * 100, 1)} %` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!m.by_point.length && <p className="px-5 pb-4 text-sm text-slate-400">Sin inspecciones en el período.</p>}
          </Card>
          <Card className="p-5" data-testid="metrics-operator-performance">
            <h3 className="font-display text-lg font-bold">Rendimiento por operario</h3>
            <p className="mt-2 text-sm text-slate-400">Disponible en la próxima versión</p>
          </Card>
        </>
      )}
    </div>
  );
}
